import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Button,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PhoneIcon from '@mui/icons-material/Phone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CallIcon from '@mui/icons-material/Call';
import RefreshIcon from '@mui/icons-material/Refresh';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import ApiService from '../../../../services/api';
import logger from '../../../../utils/logger';
import { formatDate } from '../../../../utils/dateUtils';
import { getSession } from '../../../../utils/authUtils';

const Container = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: '#f5f7fa',
  minHeight: '100vh',
}));

const StatsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: '16px',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

const WelcomeCallDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [leads, setLeads] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Complete dialog state
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Get current user info
  const session = getSession();
  const currentUser = session?.user;
  const designation = currentUser?.designation?.toLowerCase() || '';
  // Inside sales team check - includes BDM Sales (inside), CRO, and legacy 'telly'
  const isInsideSales = designation.includes('inside') || designation.includes('cro') || designation === 'telly';

  // Fetch leads/clients
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        // Fetch all leads that are clients (have been converted)
        const response = await ApiService.get('/admin/leads/get-leads?type=client');
        const data = response?.data?.leads || response?.leads || response?.data || [];
        const list = Array.isArray(data) ? data : [];

        // If inside sales team, filter to only show their assigned leads
        let filteredList = list;
        if (isInsideSales && currentUser?.id) {
          filteredList = list.filter(lead =>
            lead.telly_employee_id === currentUser.id ||
            lead.employee_id === currentUser.id
          );
        }

        setLeads(filteredList);
      } catch (err) {
        logger.error('Error fetching welcome call data:', err);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [refreshTrigger, isInsideSales, currentUser?.id]);

  // Filter leads based on tab and search
  const filteredLeads = useMemo(() => {
    let result = leads;

    // Tab filter
    switch (activeTab) {
      case 0: // Pending
        result = result.filter(lead => !lead.welcome_call);
        break;
      case 1: // Completed
        result = result.filter(lead => lead.welcome_call);
        break;
      default: // All
        break;
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(lead =>
        (lead.contact_person || '').toLowerCase().includes(query) ||
        (lead.business_name || '').toLowerCase().includes(query) ||
        (lead.phone_number || '').includes(query) ||
        (lead.email || '').toLowerCase().includes(query)
      );
    }

    return result;
  }, [leads, activeTab, searchQuery]);

  // Stats
  const stats = useMemo(() => ({
    total: leads.length,
    pending: leads.filter(l => !l.welcome_call).length,
    completed: leads.filter(l => l.welcome_call).length,
  }), [leads]);

  // Handle welcome call completion
  const handleOpenCompleteDialog = (lead) => {
    setSelectedLead(lead);
    setCompleteDialogOpen(true);
  };

  const handleCompleteWelcomeCall = async () => {
    if (!selectedLead) return;

    setCompleting(true);
    try {
      await ApiService.put(`/admin/leads/update-leads/${selectedLead.id}`, {
        welcome_call: true
      });

      setSnackbarMessage('Welcome call marked as completed!');
      setSnackbarOpen(true);
      setCompleteDialogOpen(false);
      setRefreshTrigger(prev => prev + 1);
    } catch (err) {
      logger.error('Failed to complete welcome call:', err);
      setSnackbarMessage('Failed to update. Please try again.');
      setSnackbarOpen(true);
    } finally {
      setCompleting(false);
    }
  };

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  return (
    <Container>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 20px rgba(139, 92, 246, 0.3)',
            }}
          >
            <CallIcon sx={{ color: 'white', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700} color="text.primary">
              Welcome Call Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track and manage welcome calls for new clients
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
        >
          Refresh
        </Button>
      </Box>


      {/* Main Content */}
      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        {/* Tabs and Search */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
            <Tab label={`Pending (${stats.pending})`} />
            <Tab label={`Completed (${stats.completed})`} />
            <Tab label={`All (${stats.total})`} />
          </Tabs>
          <TextField
            size="small"
            placeholder="Search clients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ width: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
        ) : filteredLeads.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <Typography color="text.secondary">No clients found</Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Client</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Date Added</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredLeads
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((lead) => (
                      <TableRow key={lead.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ p: 1, borderRadius: 1, bgcolor: '#f3f4f6' }}>
                              <BusinessIcon sx={{ color: '#6b7280' }} />
                            </Box>
                            <Box>
                              <Typography fontWeight={600}>{lead.business_name || 'N/A'}</Typography>
                              <Typography variant="caption" color="text.secondary">{lead.lead_uid || lead.id}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon sx={{ color: '#9ca3af', fontSize: 18 }} />
                            <Typography>{lead.contact_person || 'N/A'}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PhoneIcon sx={{ color: '#9ca3af', fontSize: 18 }} />
                            <Typography>{lead.phone_number || 'N/A'}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {lead.createdAt ? formatDate(lead.createdAt) : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={lead.welcome_call ? 'Completed' : 'Pending'}
                            color={lead.welcome_call ? 'success' : 'warning'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          {!lead.welcome_call && (
                            <Button
                              variant="contained"
                              size="small"
                              color="success"
                              startIcon={<CheckCircleIcon />}
                              onClick={() => handleOpenCompleteDialog(lead)}
                              sx={{ borderRadius: 2 }}
                            >
                              Mark Complete
                            </Button>
                          )}
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/admin-dashboard/client/${lead.id}`)}
                            sx={{ ml: 1 }}
                          >
                            <PersonIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredLeads.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </>
        )}
      </Paper>

      {/* Complete Dialog */}
      <Dialog open={completeDialogOpen} onClose={() => setCompleteDialogOpen(false)}>
        <DialogTitle>Confirm Welcome Call Completion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to mark the welcome call as completed for{' '}
            <strong>{selectedLead?.business_name || selectedLead?.contact_person}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleCompleteWelcomeCall}
            disabled={completing}
          >
            {completing ? <CircularProgress size={20} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSnackbarOpen(false)}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default WelcomeCallDashboard;
